<template>
  <div class="pricing-area ptb-100 bg-gondola">
    <div class="container mw-1356">
      <div
        class="section-title mw-860 m-auto text-center"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="100"
      >
        <span class="top-title">PRICING PLANS</span>
        <h2 class="text-white">Our Affordable Pricing Plans</h2>
      </div>

      <div v-if="loadingPackages" class="text-center text-white py-5">
        Loading plans...
      </div>

      <div v-else class="row justify-content-center">
        <div
          v-for="(pkg, index) in packages"
          :key="pkg.id"
          class="col-xl-4 col-md-6"
          :class="{ 'mt-4 mt-xl-0': index !== 0 }"
          data-aos="fade-up"
          data-aos-duration="1000"
          :data-aos-delay="100 * (index + 1)"
        >
          <div class="pricing-single-item style-two">
            <span class="ignite d-block">{{ pkg.duration_days }} Day Access</span>
            <div
              class="pricing-content bg-mg"
              :style="{ backgroundImage: 'url(' + pricingBgs[index % pricingBgs.length] + ')' }"
            >
              <img :src="pkg.image_url || pricingImages[index % pricingImages.length]" :alt="pkg.name" />
              <div class="amount">
                <span class="months">{{ pkg.name }}</span>
                <h2>${{ pkg.price }}</h2>
                <span class="renew">No Auto-renew</span>
              </div>
              <ul class="ps-0 list-unstyled pricing-list">
                <li
                  v-for="(feature, i) in (pkg.features || [])"
                  :key="i"
                  class="d-flex justify-content-between align-items-center"
                >
                  <span>{{ feature }}</span>
                  <i class="ti ti-circle-check"></i>
                </li>
              </ul>
              <button
                class="btn active w-100"
                :disabled="checkingOut === pkg.id"
                @click="subscribe(pkg)"
              >
                <span class="border-style">
                  {{ checkingOut === pkg.id ? "Opening..." : "Subscribe Now" }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="col-lg-12 text-center mt-4 mt-lg-5">
          <p class="text-silver">
            <strong>100%</strong> money-back guarantee for your first 24 Hours.
            <RouterLink to="/contact-us" class="read-more style-two text-grey-teal">
              Learn More <i class="ti ti-arrow-right"></i>
            </RouterLink>
          </p>
        </div>
      </div>
    </div>

    <!-- Auth modal -->
    <div v-if="authModal.open" class="pay-overlay" @click.self="authModal.open = false">
      <div class="pay-dialog" style="max-width:400px">
        <button class="pay-dialog-close-abs" @click="authModal.open = false">✕</button>

        <!-- Package reminder -->
        <div class="auth-pkg-badge" v-if="pendingPkg">
          <i class="ti ti-package"></i>
          {{ pendingPkg.name }} — ${{ pendingPkg.price }}
        </div>

        <!-- Tabs -->
        <div class="auth-tabs">
          <button :class="['auth-tab', { active: authModal.tab === 'login' }]" @click="authModal.tab = 'login'">Sign In</button>
          <button :class="['auth-tab', { active: authModal.tab === 'register' }]" @click="authModal.tab = 'register'">Create Account</button>
        </div>

        <div v-if="authModal.error" class="auth-error">{{ authModal.error }}</div>

        <!-- Login form -->
        <form v-if="authModal.tab === 'login'" @submit.prevent="submitAuth" class="auth-form">
          <div class="auth-field">
            <label>Email</label>
            <input v-model="authModal.email" type="email" required autocomplete="email" placeholder="you@example.com" />
          </div>
          <div class="auth-field">
            <label>Password</label>
            <input v-model="authModal.password" type="password" required autocomplete="current-password" placeholder="••••••••" />
          </div>
          <button type="submit" class="btn auth-submit" :disabled="authModal.loading">
            <span class="border-style">{{ authModal.loading ? "Signing in..." : "Sign In & Subscribe" }}</span>
          </button>
        </form>

        <!-- Register form -->
        <form v-else @submit.prevent="submitAuth" class="auth-form">
          <div class="auth-field">
            <label>Full Name</label>
            <input v-model="authModal.name" type="text" required autocomplete="name" placeholder="John Doe" />
          </div>
          <div class="auth-field">
            <label>Email</label>
            <input v-model="authModal.email" type="email" required autocomplete="email" placeholder="you@example.com" />
          </div>
          <div class="auth-field">
            <label>Phone Number <span style="color:#666;font-weight:400">(optional)</span></label>
            <input v-model="authModal.phone" type="tel" autocomplete="tel" placeholder="+1 234 567 8900" />
          </div>
          <div class="auth-field">
            <label>Password</label>
            <input v-model="authModal.password" type="password" required autocomplete="new-password" placeholder="Min. 8 characters" minlength="8" />
          </div>
          <button type="submit" class="btn auth-submit" :disabled="authModal.loading">
            <span class="border-style">{{ authModal.loading ? "Creating account..." : "Create Account & Subscribe" }}</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Contact-to-pay dialog -->
    <div v-if="contactDialog.open" class="pay-overlay" @click.self="contactDialog.open = false">
      <div class="pay-dialog" style="max-width:440px">
        <button class="pay-dialog-close-abs" @click="contactDialog.open = false">✕</button>

        <!-- Plan badge -->
        <div class="pay-dialog-icon" style="background:rgba(201,14,14,0.15)">
          <i class="ti ti-package" style="color:#c90e0e"></i>
        </div>
        <h4 class="pay-dialog-title">Almost there!</h4>

        <div class="ctd-plan-badge" v-if="contactDialog.pkg">
          {{ contactDialog.pkg.name }} — ${{ contactDialog.pkg.price }}
        </div>

        <p class="pay-dialog-desc">
          Pay instantly via our secure payment link, or reach out to us on WhatsApp and we'll get you set up right away.
        </p>

        <div class="ctd-contacts">
          <button
            v-if="contactDialog.pkg"
            class="ctd-contact-btn ctd-pay"
            :disabled="checkingOut === contactDialog.pkg.id"
            @click="doCheckout(contactDialog.pkg)"
          >
            <i class="ti ti-wallet"></i>
            {{ checkingOut === contactDialog.pkg?.id ? 'Opening...' : 'Pay with Crypto / Wallet' }}
          </button>

          <!-- Card: first tap reveals the short KYC form, then pay -->
          <button
            v-if="contactDialog.pkg && !contactDialog.showCardForm"
            class="ctd-contact-btn ctd-card"
            @click="contactDialog.showCardForm = true"
          >
            <i class="ti ti-credit-card"></i>
            Pay by Card (Visa / Mastercard)
          </button>

          <div v-else-if="contactDialog.pkg" class="ctd-card-form">
            <div class="ctd-kyc-row">
              <div class="ctd-kyc-field">
                <label>First Name <span class="ctd-req">*</span></label>
                <input v-model.trim="contactDialog.firstName" type="text" autocomplete="given-name" class="ctd-kyc-input" placeholder="John" />
              </div>
              <div class="ctd-kyc-field">
                <label>Last Name <span class="ctd-req">*</span></label>
                <input v-model.trim="contactDialog.lastName" type="text" autocomplete="family-name" class="ctd-kyc-input" placeholder="Smith" />
              </div>
            </div>
            <div class="ctd-kyc-row">
              <div class="ctd-kyc-field">
                <label>Date of Birth <span class="ctd-req">*</span></label>
                <input v-model="contactDialog.dob" type="date" :max="maxDob" class="ctd-kyc-input" />
              </div>
              <div class="ctd-kyc-field">
                <label>Phone <span class="ctd-req">*</span></label>
                <input v-model.trim="contactDialog.phone" type="tel" autocomplete="tel" class="ctd-kyc-input" placeholder="+1 555 123 4567" />
              </div>
            </div>
            <div class="ctd-kyc-row">
              <div class="ctd-kyc-field">
                <label>Country <span class="ctd-req">*</span></label>
                <select v-model="contactDialog.country" class="ctd-kyc-input">
                  <option value="">Select…</option>
                  <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
                </select>
              </div>
            </div>
            <div v-if="contactDialog.country === 'US'" class="ctd-kyc-row">
              <div class="ctd-kyc-field">
                <label>State <span class="ctd-req">*</span></label>
                <select v-model="contactDialog.state" class="ctd-kyc-input">
                  <option value="">Select…</option>
                  <option v-for="s in usStates" :key="s.code" :value="s.code">{{ s.name }}</option>
                </select>
              </div>
              <div class="ctd-kyc-field">
                <label>ZIP / Postal Code <span class="ctd-req">*</span></label>
                <input v-model.trim="contactDialog.postalCode" type="text" inputmode="numeric" class="ctd-kyc-input" placeholder="e.g. 10001" />
              </div>
            </div>
            <p class="ctd-kyc-note"><i class="ti ti-info-circle"></i> These details are required by our card processor for secure payment. You must be 18 or older.</p>
            <div v-if="contactDialog.cardError" class="ctd-kyc-err">{{ contactDialog.cardError }}</div>
            <button
              class="ctd-contact-btn ctd-card"
              :disabled="checkingOut === contactDialog.pkg.id"
              @click="doCheckout(contactDialog.pkg, 'card')"
            >
              <i class="ti ti-credit-card"></i>
              {{ checkingOut === contactDialog.pkg?.id ? 'Opening...' : 'Continue to Card Payment' }}
            </button>
          </div>

          <a
            v-if="contactInfo.whatsapp_number || contactInfo.phone"
            :href="'https://wa.me/' + (contactInfo.whatsapp_number || contactInfo.phone).replace(/\D/g,'') + '?text=' + encodeURIComponent('Hi! I\'d like to subscribe to the ' + (contactDialog.pkg?.name || '') + ' plan ($' + (contactDialog.pkg?.price || '') + '). Please assist.')"
            target="_blank"
            rel="noopener"
            class="ctd-contact-btn ctd-wa"
          >
            <i class="ti ti-brand-whatsapp"></i> WhatsApp Us
          </a>
        </div>

        <button class="pay-dialog-done" @click="contactDialog.open = false">Close</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import api from "@/services/api";
import { useAuthStore } from "@/store/auth";
import { useContent } from "@/composables/useContent";
import { useRecaptcha } from "@/composables/useRecaptcha";
import { RouterLink } from "vue-router";

interface Package {
  id: number;
  name: string;
  price: string;
  duration_days: number;
  features: string[] | null;
  image_url?: string;
}

export default defineComponent({
  name: "PricingPlans",
  setup() {
    const packages = ref<Package[]>([]);
    const loadingPackages = ref(true);
    const checkingOut = ref<number | null>(null);
    const authStore = useAuthStore();
    const pendingPkg = ref<Package | null>(null);
    const { getToken } = useRecaptcha();

    // Auth modal
    const authModal = reactive({
      open: false,
      tab: "login" as "login" | "register",
      name: "",
      email: "",
      phone: "",
      password: "",
      loading: false,
      error: "",
    });

    // Contact-to-pay dialog (age/country collected for card KYC, shown on demand)
    const contactDialog = reactive({
      open: false,
      pkg: null as Package | null,
      showCardForm: false,
      firstName: "",
      lastName: "",
      dob: "",
      phone: "",
      country: "",
      state: "",
      postalCode: "",
      cardError: "",
    });

    // Latest DOB allowed = today minus 18 years (card processor requires 18+).
    const maxDob = (() => {
      const t = new Date();
      const d = new Date(t.getFullYear() - 18, t.getMonth(), t.getDate());
      return d.toISOString().slice(0, 10);
    })();

    // US states — show full names, submit the 2-letter code PayEra requires.
    const usStates = [
      { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
      { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
      { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
      { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
      { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
      { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
      { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
      { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
      { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
      { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
      { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
      { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
      { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
      { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
      { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
      { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
      { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" },
    ];

    // Common countries for the card KYC selector (ISO 3166-1 alpha-2).
    const countries = [
      { code: "GB", name: "United Kingdom" }, { code: "US", name: "United States" },
      { code: "KE", name: "Kenya" }, { code: "NG", name: "Nigeria" },
      { code: "ZA", name: "South Africa" }, { code: "GH", name: "Ghana" },
      { code: "UG", name: "Uganda" }, { code: "TZ", name: "Tanzania" },
      { code: "CA", name: "Canada" }, { code: "DE", name: "Germany" },
      { code: "FR", name: "France" }, { code: "IE", name: "Ireland" },
      { code: "NL", name: "Netherlands" }, { code: "ES", name: "Spain" },
      { code: "IT", name: "Italy" }, { code: "AU", name: "Australia" },
      { code: "IN", name: "India" }, { code: "AE", name: "United Arab Emirates" },
    ];
    const { content: contactContent } = useContent("contact", {
      email: "", phone: "", telegram: "", whatsapp_number: "",
    });
    const contactInfo = computed(() => contactContent.value);

    // Images
    const defaultBgs = [
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-bg-1.jpg"),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-bg-2.jpg"),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-bg-3.jpg"),
    ];
    const defaultCardImgs = [
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-1.png"),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-2.png"),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@/assets/images/pricing-3.png"),
    ];

    const { content: pricingContent } = useContent("pricing", {
      bg_images: [{ image_url: "" }, { image_url: "" }, { image_url: "" }],
      card_images: [{ image_url: "" }, { image_url: "" }, { image_url: "" }],
    });

    const pricingBgs = computed(() =>
      pricingContent.value.bg_images.map((item: any, i: number) => item.image_url || defaultBgs[i % defaultBgs.length])
    );
    const pricingImages = computed(() =>
      pricingContent.value.card_images.map((item: any, i: number) => item.image_url || defaultCardImgs[i % defaultCardImgs.length])
    );

    onMounted(async () => {
      try {
        const { data } = await api.get("/api/v1/packages");
        packages.value = data.data;
      } catch {
        // fall through
      } finally {
        loadingPackages.value = false;
      }
    });

    function openContactDialog(pkg: Package) {
      contactDialog.pkg = pkg;
      contactDialog.showCardForm = false;
      // Prefill from the account where we can; the customer confirms/completes.
      const u = authStore.user as { name?: string; phone?: string } | null;
      const parts = (u?.name || "").trim().split(/\s+/);
      contactDialog.firstName = parts[0] || "";
      contactDialog.lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
      contactDialog.phone = u?.phone || "";
      contactDialog.dob = "";
      contactDialog.country = "";
      contactDialog.state = "";
      contactDialog.postalCode = "";
      contactDialog.cardError = "";
      contactDialog.open = true;
    }

    async function doCheckout(pkg: Package, gateway: "pdcash" | "card" | "lipad" | "payera" = "pdcash") {
      const isCard = gateway !== "pdcash";

      // Card processors require the full customer profile (KYC) — collect it.
      if (isCard) {
        const d = contactDialog;
        if (!d.firstName || !d.lastName) {
          d.cardError = "Please enter your first and last name.";
          return;
        }
        if (!d.dob || d.dob > maxDob) {
          d.cardError = "Please enter a valid date of birth (you must be 18 or older).";
          return;
        }
        if (!/^\+?\d{7,15}$/.test(d.phone.replace(/[\s-]/g, ""))) {
          d.cardError = "Please enter a valid phone number in international format (e.g. +1 555 123 4567).";
          return;
        }
        if (!d.country) {
          d.cardError = "Please select your country.";
          return;
        }
        if (d.country === "US" && !d.state) {
          d.cardError = "Please select your state.";
          return;
        }
        if (d.country === "US" && !/^\d{5}(-\d{4})?$/.test(d.postalCode)) {
          d.cardError = "Please enter a valid US ZIP code (e.g. 10001).";
          return;
        }
      }
      contactDialog.cardError = "";

      checkingOut.value = pkg.id;
      try {
        const body: Record<string, unknown> = { package_id: pkg.id, gateway };
        if (isCard) {
          const d = contactDialog;
          body.first_name = d.firstName;
          body.last_name = d.lastName;
          body.date_of_birth = d.dob;
          body.phone = "+" + d.phone.replace(/[^\d]/g, "");
          body.country = d.country;
          if (d.country === "US") {
            body.state = d.state;
            body.postal_code = d.postalCode;
          }
        }
        const { data } = await api.post("/api/v1/payments/checkout", body);
        const url = data.checkout_url || data.url;
        if (url) window.open(url, "_blank");
      } catch (e: any) {
        contactDialog.cardError =
          e?.response?.data?.message || "Could not start payment. Please try again.";
      } finally {
        checkingOut.value = null;
      }
    }

    function subscribe(pkg: Package) {
      if (!authStore.isLoggedIn) {
        pendingPkg.value = pkg;
        authModal.tab = "login";
        authModal.name = "";
        authModal.email = "";
        authModal.phone = "";
        authModal.password = "";
        authModal.error = "";
        authModal.open = true;
        return;
      }
      openContactDialog(pkg);
    }

    async function submitAuth() {
      authModal.loading = true;
      authModal.error = "";
      try {
        const recaptcha_token = await getToken(authModal.tab === "login" ? "login" : "register");
        if (authModal.tab === "login") {
          await authStore.login(authModal.email, authModal.password, recaptcha_token);
        } else {
          await authStore.register(authModal.name, authModal.email, authModal.password, recaptcha_token, authModal.phone);
        }
        authModal.open = false;
        if (pendingPkg.value) {
          openContactDialog(pendingPkg.value);
          pendingPkg.value = null;
        }
      } catch (e: any) {
        authModal.error =
          e?.response?.data?.message ||
          (authModal.tab === "login" ? "Invalid email or password." : "Registration failed. Please try again.");
      } finally {
        authModal.loading = false;
      }
    }

    return {
      packages, loadingPackages, checkingOut,
      subscribe, submitAuth, doCheckout,
      pricingBgs, pricingImages, pricingContent,
      pendingPkg, authModal,
      contactDialog, contactInfo, countries, usStates, maxDob,
    };
  },
});
</script>

<style scoped>
/* Card KYC form in the pay dialog (revealed when "Pay by Card" is tapped) */
.ctd-card-form {
  width: 100%;
  text-align: left;
  background: rgba(148,163,184,0.06);
  border: 1px solid rgba(148,163,184,0.18);
  border-radius: 10px;
  padding: 0.85rem;
}
.ctd-card-form .ctd-contact-btn {
  margin-top: 0.7rem;
}
.ctd-kyc-row {
  display: flex;
  gap: 0.75rem;
}
.ctd-kyc-field {
  flex: 1;
  min-width: 0;
}
.ctd-kyc-field label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #cbd5e1;
  margin-bottom: 0.3rem;
}
.ctd-req {
  color: #f87171;
}
.ctd-kyc-input {
  width: 100%;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  border: 1px solid rgba(148,163,184,0.35);
  background: rgba(15,23,42,0.6);
  color: #fff;
  font-size: 0.88rem;
}
.ctd-kyc-input:focus {
  outline: none;
  border-color: #c90e0e;
}
.ctd-kyc-note {
  font-size: 0.72rem;
  color: #94a3b8;
  margin: 0.45rem 0 0;
}
.ctd-kyc-err {
  font-size: 0.8rem;
  color: #f87171;
  margin-top: 0.45rem;
}

/* Auth modal */
.auth-pkg-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(201,14,14,0.12);
  border: 1px solid rgba(201,14,14,0.3);
  color: #f87171;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 99px;
  margin-bottom: 1.25rem;
}
.auth-tabs {
  display: flex;
  border-bottom: 1px solid #3a2526;
  margin-bottom: 1.25rem;
}
.auth-tab {
  flex: 1;
  background: transparent;
  border: none;
  color: #888;
  padding: 0.6rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-family: inherit;
  transition: color 0.2s, border-color 0.2s;
}
.auth-tab.active { color: #fff; border-bottom-color: #c90e0e; }
.auth-error {
  background: rgba(201,14,14,0.12);
  border: 1px solid rgba(201,14,14,0.3);
  color: #f87171;
  font-size: 0.82rem;
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}
.auth-form { display: flex; flex-direction: column; gap: 0.85rem; }
.auth-field { display: flex; flex-direction: column; gap: 0.3rem; }
.auth-field label { color: #a0a0a0; font-size: 0.8rem; }
.auth-field input {
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  color: #fff;
  padding: 0.6rem 0.9rem;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.2s;
}
.auth-field input:focus { border-color: #c90e0e; }
.auth-submit { width: 100%; margin-top: 0.25rem; }

/* Payment dialog */
.pay-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.pay-dialog {
  position: relative;
  background: #1a0f10;
  border: 1px solid #3a2526;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 2rem 2rem 1.75rem;
  text-align: center;
}
.pay-dialog-close-abs {
  position: absolute;
  top: 1rem; right: 1rem;
  background: transparent; border: none;
  color: #888; font-size: 1rem; cursor: pointer; line-height: 1;
}
.pay-dialog-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: rgba(201,14,14,0.15);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.25rem;
  font-size: 1.75rem; color: #c90e0e;
}
.pay-dialog-title { color: #fff; font-size: 1.15rem; font-weight: 700; margin-bottom: 0.75rem; }
.pay-dialog-desc { color: #a0a0a0; font-size: 0.9rem; line-height: 1.6; margin-bottom: 1.5rem; }
.pay-dialog-btn { display: block; width: 100%; margin-bottom: 0.75rem; text-decoration: none; }
.pay-dialog-done {
  background: transparent; border: 1px solid #3a2526; border-radius: 8px;
  color: #888; cursor: pointer; font-size: 0.85rem; font-family: inherit;
  padding: 0.6rem 1rem; width: 100%; transition: color 0.2s, border-color 0.2s;
}
.pay-dialog-done:hover { color: #4ade80; border-color: #4ade80; }

/* Contact-to-pay dialog */
.ctd-plan-badge {
  display: inline-block;
  background: rgba(201,14,14,0.12);
  border: 1px solid rgba(201,14,14,0.35);
  color: #f87171;
  font-size: 0.88rem;
  font-weight: 700;
  padding: 0.3rem 1rem;
  border-radius: 99px;
  margin-bottom: 1rem;
}
.ctd-contacts {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1.25rem;
}
.ctd-contact-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.15s;
  font-family: inherit;
  border: none;
  cursor: pointer;
}
.ctd-contact-btn:hover { opacity: 0.88; transform: translateY(-1px); }
.ctd-pay { background: #c90e0e; color: #fff; border: none; cursor: pointer; }
.ctd-pay:disabled { opacity: 0.6; cursor: not-allowed; }
.ctd-card { background: #1a3c8f; color: #fff; border: none; cursor: pointer; }
.ctd-card:disabled { opacity: 0.6; cursor: not-allowed; }
.ctd-wa { background: #25d366; color: #fff; }
.ctd-tg { background: #2aabee; color: #fff; }
.ctd-em { background: #3a2526; color: #e0e0e0; }
</style>
